import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Package,
  Play,
  Loader2,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Shield,
  Sparkles,
  Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddOnCompatibility = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [filterDomain, setFilterDomain] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/addon-compatibility/latest`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.log('No add-on compatibility analysis yet');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/addon-compatibility/analyze`, {});
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error:', error);
      alert('Error running add-on compatibility analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const exportBacklog = () => {
    if (!analysis) return;

    const csv = [
      ['Add-on', 'Vendor', 'Version', 'Compatibility', 'Risk', 'Strategy', 'Effort'].join(','),
      ...analysis.remediation_strategies.map(s => {
        const compat = analysis.compatibility_assessments.find(c => c.addon_catalog_id === s.addon_catalog_id);
        const risk = analysis.risk_profiles.find(r => r.addon_catalog_id === s.addon_catalog_id);
        return [
          s.product_name,
          compat?.vendor || 'Unknown',
          compat?.current_version || 'N/A',
          compat?.compatibility_state || 'UNKNOWN',
          risk?.risk_level || 'UNKNOWN',
          s.primary_strategy,
          s.effort_band
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AURA_AddOn_Compatibility_${project.name.replace(/\s/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getCompatibilityColor = (state) => {
    if (state === 'FULLY_COMPATIBLE') return 'bg-green-100 text-green-700 border-green-300';
    if (state === 'COMPATIBLE_WITH_UPGRADE') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (state === 'PARTIALLY_SUPPORTED') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (state === 'NOT_SUPPORTED') return 'bg-red-100 text-red-700 border-red-300';
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getStrategyColor = (strategy) => {
    if (strategy === 'KEEP_AS_IS') return 'bg-green-600 text-white';
    if (strategy === 'UPGRADE') return 'bg-blue-600 text-white';
    if (strategy === 'REPLACE') return 'bg-orange-600 text-white';
    if (strategy === 'RETIRE') return 'bg-red-600 text-white';
    return 'bg-purple-600 text-white';
  };

  const getRiskColor = (risk) => {
    if (risk === 'RISK_CRITICAL') return 'bg-red-600 text-white';
    if (risk === 'RISK_HIGH') return 'bg-orange-500 text-white';
    if (risk === 'RISK_MEDIUM') return 'bg-yellow-500 text-white';
    return 'bg-green-500 text-white';
  };

  const filteredStrategies = analysis?.remediation_strategies.filter(s => {
    const addon = analysis.installed_addons.find(a => a.addon_catalog_id === s.addon_catalog_id);
    const risk = analysis.risk_profiles.find(r => r.addon_catalog_id === s.addon_catalog_id);
    
    if (filterDomain !== 'all' && addon?.functional_domain !== filterDomain) return false;
    if (filterRisk !== 'all' && risk?.risk_level !== filterRisk) return false;
    return true;
  }) || [];

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Add-on & Industry Solution Compatibility</h2>
            <p className="text-gray-600">Evaluate add-on compatibility with S/4HANA target release</p>
            
            {/* SAP System Indicator */}
            {analysis && analysis.sap_system_analyzed && analysis.sap_system_analyzed.environment_name && (
              <div className="mt-3 flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-50 border border-cyan-200 rounded-lg">
                  <Package className="w-4 h-4 text-cyan-600" />
                  <span className="text-gray-700">Analyzing:</span>
                  <span className="font-bold text-cyan-600">{analysis.sap_system_analyzed.environment_name}</span>
                  <span className="text-gray-500">({analysis.sap_system_analyzed.host})</span>
                </div>
                {analysis.data_source === 'REAL_SAP_SYSTEM' && (
                  <div className="px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <span className="text-green-800 font-semibold flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>📊 Real Data</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {analysis && (
              <button 
                onClick={exportBacklog}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>Export CSV</span>
              </button>
            )}
            <button 
              onClick={runAnalysis}
              disabled={analyzing}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Run Analysis</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {!analysis && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Package className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Add-on Analysis</h3>
          <p className="text-gray-600 mb-6">Evaluate installed add-ons for S/4HANA compatibility</p>
          <button 
            onClick={runAnalysis}
            className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Run Analysis</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {analysis && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-5 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <Package className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.total_addons}</div>
              <div className="text-blue-100 text-sm">Total Add-ons</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <CheckCircle2 className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.fully_compatible}</div>
              <div className="text-green-100 text-sm">Compatible</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-6 text-white shadow-lg">
              <TrendingUp className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.needs_upgrade}</div>
              <div className="text-yellow-100 text-sm">Needs Upgrade</div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <XCircle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.not_supported}</div>
              <div className="text-red-100 text-sm">Not Supported</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.critical_risk + analysis.summary.high_risk}</div>
              <div className="text-orange-100 text-sm">High Risk</div>
            </div>
          </div>

          {/* Deployment Model Badge */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Target Deployment:</span> {analysis.deployment_model}
            </p>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'addons', label: 'Installed Add-ons', icon: Package },
                { id: 'compatibility', label: 'Compatibility', icon: CheckCircle2 },
                { id: 'strategies', label: 'Remediation', icon: Shield },
                { id: 'packages', label: 'Work Packages', icon: Sparkles }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Compatibility Status</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Fully Compatible:</span>
                          <span className="font-bold text-green-600">{analysis.summary.fully_compatible}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Needs Upgrade:</span>
                          <span className="font-bold text-blue-600">{analysis.summary.needs_upgrade}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Not Supported:</span>
                          <span className="font-bold text-red-600">{analysis.summary.not_supported}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Risk Profile</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Critical:</span>
                          <span className="font-bold text-red-600">{analysis.summary.critical_risk}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">High:</span>
                          <span className="font-bold text-orange-600">{analysis.summary.high_risk}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Add-ons:</span>
                          <span className="font-bold text-gray-900">{analysis.summary.total_addons}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Work Packages</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Packages:</span>
                          <span className="font-bold text-gray-900">{analysis.summary.work_package_count}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Effort:</span>
                          <span className="font-bold text-purple-600">{analysis.summary.total_effort_hours}h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Installed Add-ons Tab */}
              {activeTab === 'addons' && (
                <div className="space-y-4">
                  {analysis.installed_addons.map((addon) => (
                    <div key={addon.component_name} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-2">{addon.product_name}</h4>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {addon.vendor}
                            </span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              {addon.functional_domain}
                            </span>
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                              {addon.release} {addon.support_package}
                            </span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            addon.catalog_matched ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'
                          }`}>
                            {addon.catalog_matched ? 'MATCHED' : 'UNMATCHED'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Compatibility Tab */}
              {activeTab === 'compatibility' && (
                <div className="space-y-4">
                  {analysis.compatibility_assessments.map((compat) => (
                    <div key={compat.addon_catalog_id} className={`border-2 rounded-xl p-6 ${getCompatibilityColor(compat.compatibility_state)}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-1">{compat.product_name}</h4>
                          <p className="text-sm text-gray-600">Vendor: {compat.vendor} • Version: {compat.current_version}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getCompatibilityColor(compat.compatibility_state)}`}>
                          {compat.compatibility_state.replace(/_/g, ' ')}
                        </span>
                      </div>

                      <div className="bg-white rounded-lg p-4 mb-3">
                        <p className="text-sm text-gray-700">{compat.rationale}</p>
                      </div>

                      {compat.required_min_version && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                          <p className="text-sm font-semibold text-gray-900">Required for S/4HANA:</p>
                          <p className="text-sm text-gray-700">{compat.required_min_version}</p>
                        </div>
                      )}

                      {compat.replacement_suggestion && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <p className="text-sm font-semibold text-gray-900">Replacement Option:</p>
                          <p className="text-sm text-gray-700">{compat.replacement_suggestion}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Remediation Strategies Tab */}
              {activeTab === 'strategies' && (
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <select 
                      value={filterDomain}
                      onChange={(e) => setFilterDomain(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Domains</option>
                      {['Logistics', 'Finance', 'Analytics', 'Integration', 'Unknown'].map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <select 
                      value={filterRisk}
                      onChange={(e) => setFilterRisk(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Risk Levels</option>
                      <option value="RISK_CRITICAL">Critical</option>
                      <option value="RISK_HIGH">High</option>
                      <option value="RISK_MEDIUM">Medium</option>
                      <option value="RISK_LOW">Low</option>
                    </select>
                    <span className="text-sm text-gray-600">{filteredStrategies.length} add-ons</span>
                  </div>

                  {/* Strategy Cards */}
                  {filteredStrategies.map((strategy) => {
                    const risk = analysis.risk_profiles.find(r => r.addon_catalog_id === strategy.addon_catalog_id);
                    return (
                      <div key={strategy.addon_catalog_id} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStrategyColor(strategy.primary_strategy)}`}>
                                {strategy.primary_strategy.replace(/_/g, ' ')}
                              </span>
                              <h4 className="text-lg font-bold text-gray-900">{strategy.product_name}</h4>
                            </div>
                            {risk && (
                              <span className={`px-2 py-1 rounded text-xs font-bold ${getRiskColor(risk.risk_level)}`}>
                                {risk.risk_level.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-2xl font-bold text-blue-600">{strategy.effort_band}</div>
                            <div className="text-xs text-gray-500">Effort Band</div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-lg p-4 mb-3">
                          <p className="text-sm font-semibold text-gray-900 mb-2">AI Analysis:</p>
                          <p className="text-sm text-gray-700">{strategy.ai_narrative}</p>
                        </div>

                        {strategy.alternative_strategies.length > 0 && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-600 mb-2">Alternative Strategies:</p>
                            <div className="flex flex-wrap gap-2">
                              {strategy.alternative_strategies.map((alt, idx) => (
                                <span key={idx} className="px-2 py-1 bg-white border border-gray-300 text-gray-700 text-xs rounded">
                                  {alt.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Work Packages Tab */}
              {activeTab === 'packages' && (
                <div className="space-y-4">
                  {analysis.work_packages.map((pkg) => (
                    <div key={pkg.package_id} className="bg-white border-2 border-blue-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-1">{pkg.package_id}</h4>
                          <p className="text-sm text-gray-600">
                            Domain: {pkg.functional_domain} • Strategy: {pkg.primary_strategy.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600">{pkg.total_effort_hours}h</div>
                          <div className="text-xs text-gray-500">Total Effort</div>
                          <div className="text-xs text-gray-600 mt-1">Sequence: #{pkg.recommended_sequence}</div>
                        </div>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Add-ons ({pkg.addons.length}):</p>
                        <div className="flex flex-wrap gap-2">
                          {pkg.addons.map((addon, idx) => (
                            <span key={idx} className="px-2 py-1 bg-white border border-blue-300 text-blue-700 text-xs rounded">
                              {addon}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddOnCompatibility;
